import { useReducer } from 'react';

import { useDispatch } from 'react-redux';
import { doClearCreateOrder, doClearGetQuote, doClearGetQuoteChart } from 'src/actions';
import { OrderActionDto, OrderDurationDto, OrderTypeDto } from 'src/dtos';
import { SinglePageModal } from 'src/lib';
import { Position } from 'src/models';

import { TradeSecurityHoldingModalDetailsStep } from './TradeSecurityHoldingDetailsStep';
import { TradeSecurityHoldingFailedStep } from './TradeSecurityHoldingFailedStep';
import { TradeSecurityHoldingMarketPriceStep } from './TradeSecurityHoldingMarketPriceStep';
import { TradeSecurityHoldingOrderFactorStep } from './TradeSecurityHoldingOrderFactorStep';
import { TradeSecurityHoldingModalOrderStep } from './TradeSecurityHoldingOrderStep';
import { TradeSecurityHoldingModalReviewStep } from './TradeSecurityHoldingReviewStep';
import { TradeSecurityHoldingModalServiceFeeStep } from './TradeSecurityHoldingServiceFeeStep';
import { TradeSecurityHoldingSucceededStep } from './TradeSecurityHoldingSucceededStep';

enum TradeSecurityHoldingStep {
  Details = 'Details',
  Order = 'Order',
  Review = 'Review',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  MarketPrice = 'MarketPrice',
  ServiceFee = 'ServiceFee',
  OrderFactors = 'OrderFactors',
}

export interface OrderReducerState {
  step: TradeSecurityHoldingStep;
  backStep?: TradeSecurityHoldingStep;
  duration?: OrderDurationDto;
  action?: OrderActionDto;
  type?: OrderTypeDto;
  price?: number;
  stop?: number;
  quantity?: number;
  symbol?: string;
  estimatedTotal?: number;
}
const initialOrderReducerState: OrderReducerState = {
  step: TradeSecurityHoldingStep.Details,
  type: OrderTypeDto.Market,
  duration: OrderDurationDto.Day,
};

export type OrderReducerAction = { type: 'UPDATE'; payload: Partial<OrderReducerState> } | { type: 'RESET' };

const orderReducer = (state: OrderReducerState, action: OrderReducerAction) => {
  switch (action.type) {
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
      };
    case 'RESET':
      return initialOrderReducerState;
    default:
      return state;
  }
};

interface TradeSecurityHoldingModalProps {
  open: boolean;
  position: Position;
  onClose: () => void;
}

export const TradeSecurityHoldingModal = ({ open, onClose, position }: TradeSecurityHoldingModalProps) => {
  const [orderState, orderDispatch] = useReducer(orderReducer, initialOrderReducerState);
  const dispatch = useDispatch();

  const _onClose = () => {
    onClose();
    orderDispatch({ type: 'RESET' });
    dispatch(doClearCreateOrder());
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  const renderModal = () => {
    if (orderState.step === TradeSecurityHoldingStep.Order) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingModalOrderStep
            action={orderState.action}
            initialValues={orderState}
            position={position}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.Details, backStep: undefined },
              })
            }
            onReview={values => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  type: values.type,
                  duration: values.duration,
                  quantity: values.quantity ? Number(values.quantity) : undefined,
                  price: values.price ? Number(values.price) : undefined,
                  stop: values.stop ? Number(values.stop) : undefined,
                  estimatedTotal: values.estimatedTotal ? Number(values.estimatedTotal) : undefined,
                  step: TradeSecurityHoldingStep.Review,
                  backStep: TradeSecurityHoldingStep.Review,
                },
              });
            }}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.MarketPrice, backStep: TradeSecurityHoldingStep.Order },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.ServiceFee, backStep: TradeSecurityHoldingStep.Order },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.Review) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingModalReviewStep
            position={position}
            order={orderState}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.Order, backStep: TradeSecurityHoldingStep.Order },
              })
            }
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.MarketPrice, backStep: TradeSecurityHoldingStep.Review },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.ServiceFee, backStep: TradeSecurityHoldingStep.Review },
              });
            }}
            onReadMore={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.OrderFactors, backStep: TradeSecurityHoldingStep.Review },
              });
            }}
            onSuccess={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.Succeeded, backStep: undefined },
              });
            }}
            onError={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.Failed, backStep: undefined },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.MarketPrice) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingMarketPriceStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.ServiceFee) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingModalServiceFeeStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.OrderFactors) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingOrderFactorStep
            onBack={() => {
              orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } });
            }}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.Succeeded) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingSucceededStep
            position={position}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.MarketPrice, backStep: TradeSecurityHoldingStep.Succeeded },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.ServiceFee, backStep: TradeSecurityHoldingStep.Succeeded },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === TradeSecurityHoldingStep.Failed) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <TradeSecurityHoldingFailedStep
            order={orderState}
            position={position}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.MarketPrice, backStep: TradeSecurityHoldingStep.Failed },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: TradeSecurityHoldingStep.ServiceFee, backStep: TradeSecurityHoldingStep.Failed },
              });
            }}
          />
        ),
      };
    }

    return {
      width: 600,
      onClose: _onClose,
      content: (
        <TradeSecurityHoldingModalDetailsStep
          onClose={_onClose}
          position={position}
          onBuy={() => {
            orderDispatch({
              type: 'UPDATE',
              payload: { action: OrderActionDto.Buy, step: TradeSecurityHoldingStep.Order },
            });
          }}
          onSell={() => {
            orderDispatch({
              type: 'UPDATE',
              payload: { action: OrderActionDto.Sell, step: TradeSecurityHoldingStep.Order },
            });
          }}
        />
      ),
    };
  };

  return <SinglePageModal open={open} {...renderModal()} />;
};
